import { useAppDispatch, useAppSelector } from "utils/redux/hooks";
import { useCallback, useEffect } from "react";
import { trackFeatureViewed } from "utils/trackingFunctions";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Toggle from "app/components/Toggle";
import { getAllDeactivatedUsers } from "app/containers/AdminConsole/slice";
import CheckInActivityTable from "./CheckInActivityTableContainer";
import {
  getWeeklyCheckInActivityEmailReportsTableData,
  getWeeklyCheckInTableData,
  getWeeklyCheckInUserStatuses,
  selectAllWeeklyCheckInTableData,
  selectGetWeeklyCheckInTableDataStatus,
  selectWeeklyCheckInUserStatus,
  selectWeeklyCheckInActivityEmailReportsTableData,
  selectGetWeeklyCheckInActivityEmailReportsTableDataStatus,
  selectShowWeeklyCheckInSampleData,
  selectSampleWeeklyCheckInActivityData,
  setShowWeeklyCheckInSampleData,
  getSampleWeeklyCheckInActivityData,
  selectGetSampleWeeklyCheckInActivityReportStatus,
  getWeeklyCheckInCompanySettings,
} from "../slice";

const AdminCheckinActivityTable = () => {
  const dispatch = useAppDispatch();
  const fetchingWeeklyCheckInTableDataStatus = useAppSelector(
    selectGetWeeklyCheckInTableDataStatus
  );
  const sampleWeeklyCheckInActivityDataStatus = useAppSelector(
    selectGetSampleWeeklyCheckInActivityReportStatus
  );
  const sampleWeeklyCheckInActivityData = useAppSelector(
    selectSampleWeeklyCheckInActivityData
  );
  const fetchingWeeklyCheckInActivityEmailReportsTableDataStatus =
    useAppSelector(selectGetWeeklyCheckInActivityEmailReportsTableDataStatus);
  const weeklyCheckinEmailReportDataByTimeAndType = useAppSelector(
    selectWeeklyCheckInActivityEmailReportsTableData
  );
  const allWeeklyCheckInTableData = useAppSelector(
    selectAllWeeklyCheckInTableData
  );

  const weeklyCheckInUserStatus = useAppSelector(selectWeeklyCheckInUserStatus);
  const showWeeklyCheckInSampleData = useAppSelector(
    selectShowWeeklyCheckInSampleData
  );

  useEffect(() => {
    dispatch(getWeeklyCheckInUserStatuses());
    dispatch(getSampleWeeklyCheckInActivityData());
    dispatch(getWeeklyCheckInCompanySettings());
    dispatch(getAllDeactivatedUsers());
  }, [dispatch]);

  useEffect(() => {
    trackFeatureViewed("Weekly Check-In Activity Table (Admin)");
  }, []);

  const fetchCheckInCompletionTableData = useCallback(
    (params: { entityType: string; timeInterval: string }) =>
      dispatch(getWeeklyCheckInTableData(params)),
    [dispatch]
  );

  const fetchCheckInEmailReportsTableData = useCallback(
    (params: { reportType: string; timeInterval: string }) =>
      dispatch(getWeeklyCheckInActivityEmailReportsTableData(params)),
    [dispatch]
  );

  const getCheckInActivityTableProps = () => {
    if (showWeeklyCheckInSampleData) {
      return {
        title: "Check-in Activity (Sample)",
        allWeeklyCheckInTableData:
          sampleWeeklyCheckInActivityData?.weeklyCheckInTableData || {},
        getWeeklyCheckInActivityEmailReportsTableDataStatus:
          sampleWeeklyCheckInActivityDataStatus,
        getWeeklyCheckInTableDataStatus: sampleWeeklyCheckInActivityDataStatus,
        additionalContentAboveTable: (
          <div
            className="warning-banner gold"
            style={{
              top: 0,
              position: "sticky",
              zIndex: 9,
            }}
          >
            <p>
              <FontAwesomeIcon icon={["far", "info-circle"]} className="me-2" />
              <span className="fw-bold">Sample Data</span>: The data you're
              viewing is sample data and is not real. It is only intended to be
              used for demonstration purposes.
            </p>
          </div>
        ),
      };
    }

    return {
      title: "Check-in Activity",
      allWeeklyCheckInTableData: allWeeklyCheckInTableData || {},
      getWeeklyCheckInActivityEmailReportsTableDataStatus:
        fetchingWeeklyCheckInActivityEmailReportsTableDataStatus,
      getWeeklyCheckInTableDataStatus: fetchingWeeklyCheckInTableDataStatus,
    };
  };

  return (
    <CheckInActivityTable
      {...getCheckInActivityTableProps()}
      fetchCheckInCompletionTableData={fetchCheckInCompletionTableData}
      fetchCheckInEmailReportsTableData={fetchCheckInEmailReportsTableData}
      weeklyCheckinEmailReportDataByTimeAndType={
        weeklyCheckinEmailReportDataByTimeAndType
      }
      headerDescription="Showing check-in activity for everyone in your organization"
      incompleteUserAccountIds={weeklyCheckInUserStatus?.pendingUsers}
      isSample={showWeeklyCheckInSampleData}
      headerCornerContent={
        <div className="row-gap-12px align-items-center">
          <p className="grey-text">
            <b>Show Sample Data</b>
          </p>
          <Toggle
            handleToggle={() => {
              dispatch(
                setShowWeeklyCheckInSampleData(!showWeeklyCheckInSampleData)
              );
            }}
            isOn={!!showWeeklyCheckInSampleData}
          />
        </div>
      }
    />
  );
};

export default AdminCheckinActivityTable;
